import type { ImportResidentRow, ResidentRelation } from '@azko/shared';

/** A matriz e a fronteira comum entre texto colado e arquivos Excel binarios. */
export type SheetMatrix = ReadonlyArray<ReadonlyArray<unknown>>;

export interface ParsedSheet {
  rows: ImportResidentRow[];
  /** Quantidade de linhas-fonte nao vazias abaixo do cabecalho. */
  sourceRows: number;
  /** Linhas-fonte descartadas antes de chegar a API (garagem ou linha sem unidade/pessoa). */
  ignoredRows: number;
  /** Nome normalizado das colunas do cabecalho que nao foram aproveitadas. */
  ignored: string[];
  /** Decisoes automaticas que precisam aparecer na conferencia. */
  notes: string[];
  /** Erro que impede o arquivo inteiro (cabecalho faltando, por exemplo). */
  error: string | null;
}

interface SourceRow {
  cells: string[];
  /** Numero fisico que o usuario ve no Excel, incluindo a linha do cabecalho. */
  sourceLine: number;
}

function emptySheet(error: string | null = null): ParsedSheet {
  return { rows: [], sourceRows: 0, ignoredRows: 0, ignored: [], notes: [], error };
}

/** Normaliza valores de negocio sem apagar pontuacao significativa. */
function normalize(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Cabecalhos de administradoras variam em acento, caixa, pontuacao e espacos.
 * Transformar separadores em espaco faz "Prop. - CPF" e "PROP:CPF" equivalerem.
 */
function normalizeHeader(value: string): string {
  return normalize(value)
    .replace(/[ºª°]/g, '')
    .replace(/[-\u2013\u2014_./:()[\]{}\\]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

type ColumnField =
  | 'block'
  | 'unit'
  | 'name'
  | 'phone'
  | 'email'
  | 'document'
  | 'relation'
  | 'primary'
  | 'kind'
  | 'condominium';

const COLUMNS: Record<ColumnField, readonly string[]> = {
  block: ['bloco', 'block', 'torre', 'ala', 'predio'],
  unit: ['unidade', 'apartamento', 'apto', 'ap', 'apt', 'unit', 'casa', 'sala'],
  name: ['nome', 'morador', 'name', 'nome do morador'],
  phone: ['whatsapp', 'telefone', 'celular', 'fone', 'phone', 'contato'],
  email: ['email', 'e mail', 'mail'],
  document: ['cpf', 'documento', 'doc'],
  relation: ['vinculo', 'relacao', 'relation'],
  primary: ['principal', 'padrao', 'titular', 'destinatario'],
  // Na planilha larga, "tipo" e a natureza da unidade (Apto/Garagem). No
  // modelo simples antigo, o valor ainda pode ser um vinculo de pessoa.
  kind: ['tipo', 'tipo de unidade', 'natureza', 'finalidade'],
  condominium: [
    'condominio',
    'nome do condominio',
    'nome condominio',
    'condominio nome',
    'condominium',
    'empreendimento',
    'residencial',
  ],
};

const NON_RESIDENTIAL = new Set([
  'garagem',
  'vaga',
  'vaga de garagem',
  'box',
  'deposito',
  'estacionamento',
]);

const NOT_A_NAME = new Set([
  'unidade vazia',
  'imovel vazio',
  'sem ocupante',
  'nao ocupado',
  'nao ocupada',
  'unidade vaga',
  'vazia',
  'vazio',
  'vago',
  'vaga',
  'desocupada',
  'desocupado',
  'sem locatario',
  'sem morador',
  'nao informado',
  'nao consta',
  'n/a',
  'na',
  '-',
  '--',
  '---',
]);

const RELATIONS: Record<string, ResidentRelation> = {
  proprietario: 'owner',
  proprietaria: 'owner',
  propriet: 'owner',
  propr: 'owner',
  prop: 'owner',
  dono: 'owner',
  dona: 'owner',
  owner: 'owner',
  inquilino: 'tenant',
  inquilina: 'tenant',
  locatario: 'tenant',
  locataria: 'tenant',
  locat: 'tenant',
  loc: 'tenant',
  tenant: 'tenant',
  dependente: 'dependent',
  familiar: 'dependent',
  filho: 'dependent',
  filha: 'dependent',
  conjuge: 'dependent',
  dependent: 'dependent',
  funcionario: 'employee',
  funcionaria: 'employee',
  empregado: 'employee',
  empregada: 'employee',
  caseiro: 'employee',
  caseira: 'employee',
  employee: 'employee',
};

const TRUTHY = new Set(['sim', 's', 'true', '1', 'x', 'yes', 'principal']);

/** Escolhe o separador mais consistente entre as primeiras linhas preenchidas. */
function detectDelimiter(lines: string[]): string {
  const candidates = [';', '\t', ','];
  let best = ';';
  let bestScore = -1;

  for (const candidate of candidates) {
    const counts = lines.slice(0, 10).map((line) => line.split(candidate).length - 1);
    const min = Math.min(...counts);
    if (min < 1) continue;

    const consistent = counts.every((count) => count === counts[0]);
    const score = min * (consistent ? 10 : 1);
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best;
}

/** Divide uma linha respeitando campos entre aspas e aspas dobradas. */
function splitLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];

    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }

    if (char === delimiter && !quoted) {
      cells.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells.map((cell) => cell.trim());
}

function stripBlockPrefix(unit: string, block: string): string {
  if (!block) return unit;
  const prefix = normalize(block);
  const value = normalize(unit);
  if (!value.startsWith(prefix)) return unit;

  // A posicao no texto normalizado nao e necessariamente a posicao no valor
  // original: acentos e espacos repetidos mudam esse comprimento. Procurar a
  // fronteira evita transformar "Torre   A-101" em "A-101".
  for (let boundary = 1; boundary < unit.length; boundary += 1) {
    if (normalize(unit.slice(0, boundary)) !== prefix) continue;
    const rest = unit.slice(boundary);
    if (!/^[-\u2013\u2014/.\s]/.test(rest)) continue;
    const stripped = rest.replace(/^[-\u2013\u2014/.\s]+/, '').trim();
    if (stripped.length > 0) return stripped;
  }
  return unit;
}

type PersonField = 'name' | 'phone' | 'phoneAlt' | 'email' | 'document';

const PERSON_FIELDS: Record<PersonField, readonly string[]> = {
  name: ['nome completo', 'nome', 'name', 'morador'],
  // WhatsApp e Cel sao colunas diferentes no arquivo real. O WhatsApp vence;
  // Cel/telefone e apenas fallback quando ele estiver vazio.
  phone: ['whatsapp', 'whats app', 'whats', 'zap'],
  phoneAlt: ['telefone celular', 'celular', 'telefone', 'contato', 'phone', 'fone', 'tel', 'cel'],
  email: ['e mail', 'email', 'mail'],
  document: ['cpf cnpj', 'documento', 'cpf', 'doc'],
};

const PERSON_FIELD_MATCHES = (Object.entries(PERSON_FIELDS) as Array<
  [PersonField, readonly string[]]
>)
  .flatMap(([field, aliases]) => aliases.map((alias) => ({ field, alias })))
  .sort((left, right) => right.alias.length - left.alias.length);

const DESCRIPTOR_CONNECTORS = new Set(['do', 'da', 'de', 'numero', 'num', 'n', 'no', 'nro']);
const ORDINALS: Record<string, string> = {
  primeiro: '1',
  primeira: '1',
  segundo: '2',
  segunda: '2',
  terceiro: '3',
  terceira: '3',
  quarto: '4',
  quarta: '4',
};

interface PersonHeader {
  relation: ResidentRelation;
  ordinal: string | null;
  field: PersonField;
  label: string;
}

function readPersonDescriptor(raw: string): Omit<PersonHeader, 'field'> | null {
  const descriptor = normalizeHeader(raw).replace(/^(?:do|da|de)\s+/, '');
  const tokens = descriptor.split(' ').filter(Boolean);
  let relation: ResidentRelation | null = null;
  let ordinal: string | null = null;

  for (const token of tokens) {
    const tokenRelation = RELATIONS[token];
    if (tokenRelation) {
      if (relation && relation !== tokenRelation) return null;
      relation = tokenRelation;
      continue;
    }

    const numericOrdinal = /^(\d+)(?:o|a)?$/.exec(token)?.[1];
    const wordOrdinal = ORDINALS[token];
    if (numericOrdinal || wordOrdinal) {
      ordinal = String(Number(numericOrdinal ?? wordOrdinal));
      continue;
    }

    if (!DESCRIPTOR_CONNECTORS.has(token)) return null;
  }

  return relation ? { relation, ordinal, label: descriptor } : null;
}

/**
 * Reconhece tanto "Proprietario 2 - CPF" quanto "CPF do proprietario 2".
 * A correspondencia e por aliases completos, nunca por substring solta.
 */
function readPersonHeader(cell: string): PersonHeader | null {
  // Algumas administradoras usam apenas "Proprietario" para a coluna de nome.
  // Ela e inequívoca no formato largo e precisa funcionar como
  // "Nome do proprietario", inclusive com numero/abreviacao.
  const bareDescriptor = readPersonDescriptor(cell);
  if (bareDescriptor) return { ...bareDescriptor, field: 'name' };

  for (const { field, alias } of PERSON_FIELD_MATCHES) {
    if (cell.endsWith(` ${alias}`)) {
      const descriptor = readPersonDescriptor(cell.slice(0, -(alias.length + 1)));
      if (descriptor) return { ...descriptor, field };
    }

    if (cell.startsWith(`${alias} `)) {
      const descriptor = readPersonDescriptor(cell.slice(alias.length + 1));
      if (descriptor) return { ...descriptor, field };
    }
  }

  return null;
}

interface PersonColumns {
  label: string;
  relation: ResidentRelation;
  ordinal: string | null;
  columns: Partial<Record<PersonField, number>>;
}

function readPersonGroups(header: string[]): { groups: PersonColumns[]; used: Set<number> } {
  const groups: PersonColumns[] = [];
  const byKey = new Map<string, PersonColumns>();
  const fieldOccurrences = new Map<string, number>();

  header.forEach((cell, position) => {
    const parsed = readPersonHeader(cell);
    if (!parsed) return;

    // Quando o arquivo repete cabecalhos sem numero, a enesima ocorrencia de
    // cada campo pertence a enesima pessoa. Isso cobre tanto blocos contiguos
    // (Nome, CPF, Nome, CPF) quanto colunas agrupadas por campo
    // (Nome, Nome, CPF, CPF), sem misturar documentos entre proprietarios.
    const occurrenceKey = `${parsed.relation}:${parsed.field}`;
    const occurrence = parsed.ordinal
      ? Number(parsed.ordinal)
      : (fieldOccurrences.get(occurrenceKey) ?? 0) + 1;
    if (!parsed.ordinal) fieldOccurrences.set(occurrenceKey, occurrence);

    const key = `${parsed.relation}:${occurrence}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        label: parsed.ordinal ? parsed.label : `${parsed.label} ${occurrence}`,
        relation: parsed.relation,
        ordinal: parsed.ordinal,
        columns: {},
      };
      groups.push(group);
      byKey.set(key, group);
    }

    group.columns[parsed.field] = position;
  });

  const validGroups = groups.filter((group) => group.columns.name !== undefined);
  const used = new Set<number>();
  for (const group of validGroups) {
    for (const position of Object.values(group.columns)) {
      if (position !== undefined) used.add(position);
    }
  }

  return { groups: validGroups, used };
}

function textCell(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

function isFilled(row: SourceRow): boolean {
  return row.cells.some((cell) => cell.length > 0);
}

function unitKey(block: string, unit: string): string {
  return `${normalize(block)}|${normalize(unit)}`;
}

/**
 * Parser central. `sourceLineOffset` preserva o numero real quando a faixa usada
 * de uma worksheet comeca abaixo da primeira linha fisica.
 */
export function parseSheetMatrix(matrix: SheetMatrix, sourceLineOffset = 0): ParsedSheet {
  const source: SourceRow[] = matrix.map((values, index) => ({
    cells: values.map(textCell),
    sourceLine: sourceLineOffset + index + 1,
  }));
  const headerPosition = source.findIndex(isFilled);
  if (headerPosition === -1) return emptySheet();

  const sourceData = source.slice(headerPosition + 1).filter(isFilled);
  const sourceRows = sourceData.length;
  const header = (source[headerPosition]?.cells ?? []).map(normalizeHeader);
  const wide = readPersonGroups(header);

  const index: Partial<Record<ColumnField, number>> = {};
  const ignored = new Set<string>();

  header.forEach((cell, position) => {
    if (wide.used.has(position)) return;
    const match = (Object.entries(COLUMNS) as Array<[ColumnField, readonly string[]]>).find(
      ([, aliases]) => aliases.includes(cell),
    );
    if (match) {
      const [field] = match;
      if (index[field] === undefined) index[field] = position;
    } else if (cell) {
      ignored.add(cell);
    }
  });

  const hasName = wide.groups.length > 0 || index.name !== undefined;
  if (!hasName || index.unit === undefined) {
    return {
      ...emptySheet(
        'A primeira linha precisa ser o cabecalho, com pelo menos as colunas "unidade" e "nome".',
      ),
      sourceRows,
    };
  }

  const rows: ImportResidentRow[] = [];
  const vacantUnits = new Set<string>();
  const vacantRows = new Map<string, ImportResidentRow>();
  const notes: string[] = [];
  let ignoredRows = 0;
  let droppedNonResidential = 0;
  let droppedVacantMarkers = 0;
  let strippedPrefix = 0;

  for (const sourceRow of sourceData) {
    const cellAt = (position: number | undefined): string =>
      position === undefined ? '' : (sourceRow.cells[position] ?? '').trim();
    const at = (field: ColumnField): string => cellAt(index[field]);

    const block = at('block');
    const rawUnit = at('unit');
    const condominiumName = at('condominium');
    const kind = normalize(at('kind'));
    const kindAsRelation = RELATIONS[kind];

    if (!kindAsRelation && kind && NON_RESIDENTIAL.has(kind)) {
      droppedNonResidential += 1;
      ignoredRows += 1;
      continue;
    }

    const unit = stripBlockPrefix(rawUnit, block);
    if (unit !== rawUnit) strippedPrefix += 1;
    const currentUnitKey = rawUnit ? unitKey(block, unit) : '';

    // Formato largo: uma linha de unidade pode produzir varias pessoas.
    if (wide.groups.length > 0) {
      let hasValidOccupant = false;
      let hasVacancyMarker = false;
      const hasOccupantColumns = wide.groups.some((group) => group.relation !== 'owner');
      const people: ImportResidentRow[] = [];

      for (const group of wide.groups) {
        const name = cellAt(group.columns.name);
        if (!name) continue;
        if (NOT_A_NAME.has(normalize(name))) {
          droppedVacantMarkers += 1;
          if (group.relation !== 'owner') hasVacancyMarker = true;
          continue;
        }

        const phone = cellAt(group.columns.phone) || cellAt(group.columns.phoneAlt);
        const email = cellAt(group.columns.email);
        const document = cellAt(group.columns.document);
        if (group.relation !== 'owner') hasValidOccupant = true;

        people.push({
          ...(block ? { block } : {}),
          unit,
          name,
          ...(phone ? { phone } : {}),
          ...(email ? { email } : {}),
          ...(document ? { document } : {}),
          ...(condominiumName ? { condominiumName } : {}),
          relation: group.relation,
          sourceLine: sourceRow.sourceLine,
        });
      }

      const vacant =
        !hasValidOccupant &&
        (hasVacancyMarker || hasOccupantColumns || (Boolean(rawUnit) && people.length === 0));
      if (vacant && currentUnitKey) vacantUnits.add(currentUnitKey);

      if (vacant) {
        for (const person of people) {
          if (person.relation === 'owner') {
            person.vacantUnit = true;
          }
        }
      }

      if (people.length === 0) {
        if (vacant && rawUnit) {
          vacantRows.set(currentUnitKey, {
            ...(block ? { block } : {}),
            unit,
            name: '',
            ...(condominiumName ? { condominiumName } : {}),
            vacantUnit: true,
            sourceLine: sourceRow.sourceLine,
          });
        } else {
          ignoredRows += 1;
        }
        continue;
      }

      // `isPrimary` e destinatario de notificacao, nao proprietario principal.
      // Em unidade ocupada, vence quem realmente pode receber o aviso (WhatsApp
      // antes de e-mail), mesmo que seja o proprietario. Em unidade somente
      // com varios proprietarios, nenhum deles e escolhido ao acaso.
      const firstOccupant = people.findIndex((person) => person.relation !== 'owner');
      const withPhone = people.findIndex((person) => person.phone);
      const withEmail = people.findIndex((person) => person.email);
      const chosen =
        firstOccupant !== -1
          ? withPhone !== -1
            ? withPhone
            : withEmail !== -1
              ? withEmail
              : firstOccupant
          : people.length === 1
            ? 0
            : -1;
      rows.push(
        ...people.map((person, position) =>
          position === chosen ? { ...person, isPrimary: true } : person,
        ),
      );
      continue;
    }

    // Formato simples: uma linha representa uma pessoa.
    const name = at('name');
    if (!name && !rawUnit) {
      ignoredRows += 1;
      continue;
    }

    if (name && NOT_A_NAME.has(normalize(name))) {
      droppedVacantMarkers += 1;
      if (currentUnitKey) {
        vacantUnits.add(currentUnitKey);
        vacantRows.set(currentUnitKey, {
          ...(block ? { block } : {}),
          unit,
          name: '',
          ...(condominiumName ? { condominiumName } : {}),
          vacantUnit: true,
          sourceLine: sourceRow.sourceLine,
        });
      } else {
        // A API devolve o numero da linha e explica que a unidade e obrigatoria.
        rows.push({ unit, name: '', vacantUnit: true, sourceLine: sourceRow.sourceLine });
      }
      continue;
    }

    if (!name && rawUnit) {
      vacantUnits.add(currentUnitKey);
      vacantRows.set(currentUnitKey, {
        ...(block ? { block } : {}),
        unit,
        name: '',
        ...(condominiumName ? { condominiumName } : {}),
        vacantUnit: true,
        sourceLine: sourceRow.sourceLine,
      });
      continue;
    }

    const relation = RELATIONS[normalize(at('relation'))] ?? kindAsRelation;
    const primary = at('primary');
    const phone = at('phone');
    const email = at('email');
    const document = at('document');

    rows.push({
      ...(block ? { block } : {}),
      unit,
      name,
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
      ...(document ? { document } : {}),
      ...(condominiumName ? { condominiumName } : {}),
      ...(relation ? { relation } : {}),
      ...(primary ? { isPrimary: TRUTHY.has(normalize(primary)) } : {}),
      sourceLine: sourceRow.sourceLine,
    });
  }

  // No modelo simples, o marcador de vazio e o proprietario podem ocupar linhas
  // diferentes. A chave da unidade permite transportar a informacao ate ele.
  for (const row of rows) {
    if (
      (row.relation ?? 'owner') === 'owner' &&
      vacantUnits.has(unitKey(row.block ?? '', row.unit))
    ) {
      row.vacantUnit = true;
      vacantRows.delete(unitKey(row.block ?? '', row.unit));
    }
  }

  // Sem proprietario em qualquer linha, a unidade ainda segue para o backend:
  // ela pode ser criada e volta com um erro claro de responsavel ausente.
  rows.push(...vacantRows.values());
  const emptyWithoutOwner = vacantRows.size;

  if (wide.groups.length > 0) {
    notes.push(
      `Planilha com ${wide.groups.length} grupos de pessoas por linha (${wide.groups
        .map((group) => group.label)
        .join(', ')}) — cada pessoa virou um registro.`,
    );
  }
  if (droppedNonResidential > 0) {
    notes.push(
      `${droppedNonResidential} linhas ignoradas por nao serem moradia (garagem, vaga, deposito).`,
    );
  }
  if (droppedVacantMarkers > 0) {
    notes.push(
      `${droppedVacantMarkers} marcadores de unidade vazia foram reconhecidos; o proprietario valido foi mantido.`,
    );
  }
  if (emptyWithoutOwner > 0) {
    notes.push(
      `${emptyWithoutOwner} unidades vazias nao tinham proprietario valido e nao puderam ser associadas.`,
    );
  }
  if (strippedPrefix > 0) {
    notes.push(`${strippedPrefix} unidades tiveram o nome do bloco removido do rotulo.`);
  }

  return {
    rows,
    sourceRows,
    ignoredRows,
    ignored: [...ignored],
    notes,
    error: null,
  };
}

/** Texto colado, CSV ou TSV. Mantem linhas vazias para preservar sourceLine. */
export function parseSheet(text: string): ParsedSheet {
  if (!text.trim()) return emptySheet();
  const lines = text.split(/\r\n|\n|\r/);
  const delimiter = detectDelimiter(lines.filter((line) => line.trim()).slice(0, 10));
  return parseSheetMatrix(lines.map((line) => splitLine(line, delimiter)));
}

/**
 * Le XLS/XLSX somente quando o usuario escolhe um arquivo Excel. O import
 * dinamico impede o parser binario de aumentar o bundle inicial da aplicacao.
 */
export async function parseWorkbook(data: ArrayBuffer): Promise<ParsedSheet> {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(data, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return emptySheet('O arquivo Excel nao possui nenhuma aba.');

  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) return emptySheet('Nao foi possivel ler a primeira aba do arquivo Excel.');

  const range = worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null;
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    header: 1,
    raw: false,
    defval: '',
    blankrows: true,
  });
  const parsed = parseSheetMatrix(matrix, range?.s.r ?? 0);

  if (workbook.SheetNames.length <= 1) return parsed;
  return {
    ...parsed,
    notes: [`Apenas a primeira aba ("${sheetName}") foi lida.`, ...parsed.notes],
  };
}

/** Modelo que a tela oferece para quem nao sabe por onde comecar. */
export const SHEET_EXAMPLE = [
  'bloco;unidade;nome;cpf;whatsapp;email;vinculo',
  'Torre A;101;Gabriel Aguiar;529.982.247-25;31 99999-1122;;proprietario',
  'Torre A;101;Maria Aguiar;;31 98888-2211;;dependente',
  'Torre B;101;Joao Silva;;31 97777-3311;;inquilino',
].join('\n');
